import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../../lib/api";
import { Tent, ArrowRight, MapPin } from "lucide-react";

/**
 * "Wild & hidden" discovery rail.
 *
 * Surfaces 6 OSM-sourced campsites that are NOT bookable on Pitchup — the
 * off-grid spots only crowdsourced data knows about. Replaces the old
 * "everything we have is also on Pitchup" framing with something that
 * genuinely differentiates CampCompass.
 *
 * Light-touch query: hits /api/campsites with source=openstreetmap_overpass
 * and pitchup_listed=false. Falls back gracefully if the endpoint doesn't
 * accept those params (in which case it filters client-side from a small slice).
 */
export default function WildHiddenRail() {
  const { t } = useTranslation();
  const [sites, setSites] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/campsites", {
          params: { source: "openstreetmap_overpass", limit: 200, slim: true },
        });
        // Client-side: prefer the ones with proper county regions (skip leftover
        // "United Kingdom" stragglers) and randomly pick 6.
        const pool = (data || []).filter(
          (s) => s.region && s.region !== "United Kingdom" && s.pitchup_listed !== true,
        );
        const picked = [...pool].sort(() => Math.random() - 0.5).slice(0, 6);
        setSites(picked);
      } catch {
        setSites([]);
      }
    })();
  }, []);

  if (sites.length === 0) return null;

  return (
    <section className="border-b hairline bg-[#fafafa]" data-testid="wild-hidden-rail">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div className="eyebrow mb-3">{t("wild.eyebrow")}</div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight max-w-2xl">
              {t("wild.headline_a")} <span className="text-[#ff4500]">{t("wild.headline_b")}</span>
            </h2>
            <p className="mt-3 text-sm text-[#555] max-w-xl leading-snug">{t("wild.body")}</p>
          </div>
          <Link
            to="/browse?source=openstreetmap"
            className="text-sm link-underline flex items-center gap-1 text-[#ff4500]"
            data-testid="wild-explore-all"
          >
            {t("wild.exploreAll")} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sites.map((s) => (
            <Link
              key={s.campsite_id}
              to={`/campsites/${s.campsite_id}`}
              className="group border hairline bg-white overflow-hidden hover:border-[#ff4500] transition-colors flex"
              data-testid={`wild-card-${s.campsite_id}`}
            >
              <div className="w-28 h-28 sm:w-32 sm:h-32 shrink-0 bg-[#1a1a1a] relative overflow-hidden">
                {s.image && (
                  <img
                    src={s.image}
                    alt=""
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform"
                  />
                )}
              </div>
              <div className="flex-1 min-w-0 p-3 sm:p-4 flex flex-col justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-[10px] mono uppercase tracking-wider text-[#888] mb-1">
                    <MapPin className="w-3 h-3" strokeWidth={1.75} />
                    <span className="truncate">{s.region}</span>
                  </div>
                  <div className="font-display text-base leading-tight line-clamp-2">{s.name}</div>
                </div>
                <div className="mt-2 flex items-center gap-1.5 text-[10px] mono uppercase tracking-wider text-[#ff4500]">
                  <Tent className="w-3 h-3" strokeWidth={2} /> {t("wild.osmTag")}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
